import React from 'react';
import { PhoneIcon, EyeIcon, CheckCircleIcon } from 'lucide-react';
export interface Loan {
  id: string;
  borrowerName: string;
  loanNumber: string;
  fundedDate: string;
  loanType: 'FHA' | 'CONV' | 'VA' | 'USDA';
  rate: string;
  phone: string;
  status: 'pending' | 'completed';
  completedDate?: string;
  followUpNote?: string;
}
interface LoanRowProps {
  loan: Loan;
  onMarkComplete: (loan: Loan) => void;
  onView: (loan: Loan) => void;
  showActions?: boolean;
}
function getLoanTypeBadge(type: string) {
  switch (type) {
    case 'FHA':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'CONV':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'VA':
      return 'bg-violet-50 text-violet-700 border-violet-200';
    case 'USDA':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
}
export function LoanRow({
  loan,
  onMarkComplete,
  onView,
  showActions = true
}: LoanRowProps) {
  return <tr className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-id="element-27">
      <td className="py-3.5 px-4" data-id="element-28">
        <div data-id="element-29">
          <p className="text-sm font-medium text-gray-900" data-id="element-30">
            {loan.borrowerName}
          </p>
          <p className="text-xs text-gray-500" data-id="element-31">Loan #{loan.loanNumber}</p>
        </div>
      </td>
      <td className="py-3.5 px-4" data-id="element-32">
        <span className="text-sm text-gray-700" data-id="element-33">{loan.fundedDate}</span>
      </td>
      <td className="py-3.5 px-4" data-id="element-34">
        <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full border ${getLoanTypeBadge(loan.loanType)}`} data-id="element-35">
          {loan.loanType}
        </span>
      </td>
      <td className="py-3.5 px-4" data-id="element-36">
        <span className="text-sm font-medium text-gray-900" data-id="element-37">{loan.rate}</span>
      </td>
      <td className="py-3.5 px-4" data-id="element-38">
        <a href={`tel:${loan.phone.replace(/[^0-9]/g, '')}`} className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 transition-colors" aria-label={`Call ${loan.borrowerName} at ${loan.phone}`} data-id="element-39">
          <PhoneIcon className="w-3.5 h-3.5" data-id="element-40" />
          {loan.phone}
        </a>
      </td>
      <td className="py-3.5 px-4" data-id="element-41">
        <div className="flex items-center gap-2" data-id="element-42">
          <button onClick={() => onView(loan)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label={`View details for ${loan.borrowerName}`} data-id="element-43">
            <EyeIcon className="w-3.5 h-3.5" data-id="element-44" />
            View
          </button>
          {showActions && loan.status === 'pending' && <button onClick={() => onMarkComplete(loan)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2" aria-label={`Mark follow-up complete for ${loan.borrowerName}`} data-id="element-45">
              <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-46" />
              Mark Complete
            </button>}
          {loan.status === 'completed' && <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full border border-emerald-200" data-id="element-47">
              <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-48" />
              Done
            </span>}
        </div>
      </td>
    </tr>;
}